import type { AnalysisEntry } from '@/api/schemas'
import { formatLocalDate, formatLocalTime } from '@/lib/format'
import { t } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { Link } from '@tanstack/react-router'

interface Props {
  siteId: string
  entries: AnalysisEntry[]
  /** WEB-021: YYYY-MM-DD when viewing a past day, null/undefined when on today. */
  selectedDate?: string | null
  onSelectAnalysis?: (entry: AnalysisEntry) => void
}

const LEVEL_LABELS: Record<string, { label: string; className: string }> = {
  weekly: { label: '7d', className: 'bg-accent text-primary-hover' },
  daily: { label: '24h', className: 'bg-accent text-primary-hover' },
  hourly: { label: '1h', className: 'bg-accent text-primary-hover' },
}

export function AnalysesList({
  //@usedby:AnalysesList.test.tsx,SiteDashboard.tsx
  siteId,
  entries,
  selectedDate,
  onSelectAnalysis,
}: Props) {
  const heading = selectedDate ? t('Analyses on {date}', { date: selectedDate }) : t("Today's analyses")
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          {heading}
        </h2>
        <Link
          to="/sites/$siteId/alerts"
          params={{ siteId }}
          className="text-xs text-primary hover:text-primary-hover"
        >
          {t('View alerts →')}
        </Link>
      </div>
      <div className="bg-card rounded-xl border border-border divide-y divide-border-subtle">
        {entries.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-subtle-foreground">
            {t('No analyses yet')}
          </div>
        ) : (
          entries.map((entry) => (
            <AnalysisRow
              key={`${entry.type}:${entry.id}`}
              entry={entry}
              onSelect={onSelectAnalysis}
            />
          ))
        )}
      </div>
    </div>
  )
}

function AnalysisRow({
  entry,
  onSelect,
}: { entry: AnalysisEntry; onSelect?: (entry: AnalysisEntry) => void }) {
  const levelKey = entry.type === 'summary' ? entry.level : 'batch'
  const cfg = LEVEL_LABELS[levelKey] ?? {
    label: entry.type === 'batch' ? t('batch') : levelKey,
    className: 'bg-muted text-foreground-muted',
  }

  const noChange = entry.type === 'summary' && entry.no_change === true
  const relevance = entry.type === 'summary' ? (entry.relevance ?? null) : null
  const muted = noChange || relevance === 'low'
  const high = relevance === 'high'
  const medium = relevance === 'medium'
  const reason = entry.type === 'summary' ? (entry.reason ?? '') : ''
  // Differentiate "no data captured" vs "site active but nothing changed" — the
  // analyzer writes "No batches captured…" for empty hours and "Site inactive…"
  // (or similar) for idle hours. Keyword match is brittle but the only signal
  // the API surfaces today.
  const noData = noChange && reason.trim().toLowerCase().startsWith('no batches')
  const idle = noChange && !noData

  // Daily/weekly rows anchor to local midnight, so rendering their period_start
  // as HH:MM would always print "00:00". Show the date instead.
  const timeLabel =
    levelKey === 'daily' || levelKey === 'weekly'
      ? formatLocalDate(entry.period_start)
      : formatLocalTime(entry.period_start)

  let body: React.ReactNode
  if (noChange) {
    body = (
      <span className="text-xs text-subtle-foreground leading-relaxed flex-1">
        {reason || t('No change')}
      </span>
    )
  } else {
    body = (
      <span
        className={cn(
          'text-xs line-clamp-2 leading-relaxed flex-1',
          muted ? 'text-subtle-foreground' : 'text-foreground-soft',
        )}
      >
        {entry.summary || reason || '—'}
      </span>
    )
  }

  const className =
    'flex items-start gap-3 px-4 py-2.5 hover:bg-background transition-colors w-full text-left'
  const content = (
    <>
      <span className="text-xs text-subtle-foreground flex-shrink-0 tabular-nums pt-0.5">
        {timeLabel}
      </span>
      <span
        className={cn('flex-shrink-0 text-xs px-1.5 py-0.5 rounded font-medium', cfg.className)}
      >
        {cfg.label}
      </span>
      {high ? (
        <span
          className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded font-medium bg-attention/10 text-attention"
          title={t('high activity')}
        >
          ★
        </span>
      ) : medium ? (
        <span
          className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded font-medium bg-muted text-foreground-muted"
          title={t('activity detected')}
        >
          ◉
        </span>
      ) : null}
      {idle ? (
        <span
          className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded font-medium bg-muted text-foreground-muted"
          title={t('site idle — events captured but no change')}
        >
          {t('idle')}
        </span>
      ) : noData ? (
        <span
          className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded font-medium bg-muted text-subtle-foreground"
          title={t('no events captured in this window')}
        >
          {t('no data')}
        </span>
      ) : null}
      {body}
    </>
  )

  if (onSelect) {
    return (
      <button
        type="button"
        className={className}
        data-analysis-id={`${entry.type}:${entry.id}`}
        onClick={() => onSelect(entry)}
      >
        {content}
      </button>
    )
  }
  return (
    <div className={className} data-analysis-id={`${entry.type}:${entry.id}`}>
      {content}
    </div>
  )
}
